import { useSearchParams } from '@solidjs/router';
import clsx from 'clsx';
import { For, Show, createEffect, createMemo, createSignal, on, onMount } from 'solid-js';
import { useAppContext } from '../../app-context-provider/app-context-provider';
import { ButtonColorName } from '../../style/color-classes';
import { ErrorCatcher } from '../../tools/error-catcher';
import { Button } from '../../ui-components/button/button';
import { GET_POSTS_QUERY } from './blog-archive-queries';
import { BlogCategory } from './blog-archive-types';
import { BlogListItem } from './blog-list-item';
import { PostListingSchema } from '../schema/post-listing';

const tagClasses = (active?: boolean) => {
    return clsx(
        'border-1 mb-xxs border-primary-600 px-s text-primary-600 hover:bg-primary-600 hover:text-primary-100 text-medium inline-block rounded-l focus:rounded-l py-[0.31rem] transition-colors duration-200',
        { 'bg-primary-600': active },
        { '!text-primary-100': active },
    );
};

export const BlogArchive = ({blogCategoriesFromHook = [] }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const searchParamsCategory = searchParams.category;
    const { createCachedResource, translate } = useAppContext();
    const [selectedFilter, setSelectedFilter] = createSignal<string | undefined>(searchParamsCategory ? searchParamsCategory : undefined);
    const [limit]  = createSignal(20);
    const [offset, setOffset] = createSignal(0);
    const [posts, setPosts] = createSignal<any>([]);
    const [displayLoadMoreButton, setDisplayLoadMoreButton] = createSignal(false);


    const queryVars = createMemo(() => {
        return {
            tags: selectedFilter(),
            limit: limit() + 1,
            offset: offset(),
        };
    });

    const [postsContent] = createCachedResource(GET_POSTS_QUERY, queryVars, true);

    const handleLoadMoreClick = () => {
        setOffset(offset() + limit());
    };

    const handleFilterClick = (slug: string | undefined) => {
        setPosts([]);
        setOffset(0);
        setSearchParams({ category: slug }, { replace: true });
        setSelectedFilter(slug);
    };

    createEffect(on(postsContent, (content) => {
        if (!content) return;

        setDisplayLoadMoreButton(content.posts.length > limit());
        setPosts(prev => {
            if (offset() === 0) {
                return content.posts.slice(0, limit());
            }
            return [...prev, ...content.posts.slice(0, limit())];
        });
    }));

    onMount(() => {
        if (blogCategoriesFromHook?.length === 1) {
            setSelectedFilter(blogCategoriesFromHook[0]);
        }
    });

    return (
        <ErrorCatcher componentName="BlogArchive">
            <div>
                <div class="mb-l tablet:mt-xxl gap-x-xxs flex flex-wrap">
                    <button
                        aria-label={`${translate('filter-on')} ${translate('all')}`}
                        onClick={() => handleFilterClick(undefined)}
                        class={tagClasses(!searchParams.category)}>
                        {translate('all')}
                    </button>
                    <For each={blogCategoriesFromHook}>
                        {(category: BlogCategory) => (
                            <button
                                innerHTML={category.name}
                                aria-label={`${translate('filter-on')} ${category.name}`}
                                onClick={() => handleFilterClick(category.slug)}
                                class={tagClasses(searchParams.category === category.slug)}
                            >
                                {category.name}
                            </button>
                        )}
                    </For>
                </div>
                <Show when={postsContent() && posts().length > 0} fallback={<div class="h-screen"></div>}>
                    <div class="post-list tablet:grid-cols-2 laptop:grid-cols-3 gap-y-xl gap-x-l tablet:gap-y-xxl tablet:gap-x-xxl mb-xl grid grid-cols-1">
                        <For each={posts()}>{(post) => <BlogListItem post={post.content} />}</For>
                    </div>
                
                    <Show when={displayLoadMoreButton()}>
                        <div class="text-center">
                            <Button variant="solid" color={ButtonColorName.Primary} onClick={handleLoadMoreClick} label={translate('load-more')} buttonSize="small" />
                        </div>
                    </Show>
                </Show>
                <PostListingSchema
                    content={postsContent()?.posts}
                    postSingleType="BlogPosting"
                    postListingType="Blog"
                    archiveName={translate('twofour-blog-name')}
                    archiveUrl={translate('twofour-blog-url')}
                    useHeadline={true}
                />
            </div>
        </ErrorCatcher>
    );
};
